import type { IActivityOccurrenceOrGroup } from "@medoma/models/activities";
import {
  getActivityCategoryConfig,
  isGroup as isGroupCheck,
  isEmployeePerformed,
  timeOfDaySchema,
} from "@medoma/models/activities";
import styles from "./AllocatableItemTile.module.scss";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { DragHandle } from "@/components/DragAndDrop/DragHandle";
import clsx from "clsx";
import { getPatientNameWithStatus, type IListPatient } from "@/api/Patients";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import { activityRequirementSchema } from "@medoma/models/activityRequirements";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { usePlanningViewToolbar } from "../usePlanningViewToolbar";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { patientStatusSchema } from "@medoma/models/patients";
import PausedIndicator from "@/components/PausedIndicator/PausedIndicator";
import { FlagsPopover } from "@/components/FlagsPopover/FlagsPopover";
import { KeyToPatientPopover } from "@/components/KeyToPatientPopover/KeyToPatientPopover";
import { PetPopover } from "@/components/PetPopover/PetPopover";
import TeamBadge from "@/components/Teams/TeamBadge";
import { useTeams } from "@/api/Patients";
import {
  TextWithEllipsis,
  TextWithLineBreaks,
} from "@medoma/components/Text/Text";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import DurationChip from "@/components/Chips/DurationChip";
import { calculateGroupDuration } from "@/components/Chips/calculateGroupDuration";

const PatientName = ({
  patient,
  showPausedIndicator = true,
}: {
  patient: IListPatient;
  showPausedIndicator?: boolean;
}) => {
  const patientName = getPatientNameWithStatus(patient, "full");

  const showPaused =
    showPausedIndicator &&
    patient &&
    patient.status === patientStatusSchema.enum.admitted &&
    patient.pausedAt;
  const showFlags = patient && "flags" in patient && !!patient.flags;
  const showKeyToPatient =
    patient && "address" in patient && !!patient.address.keyToPatient;
  const showPet = patient && "pet" in patient && !!patient.pet;
  const hasPatientInfoIndicators =
    showPaused || showFlags || showKeyToPatient || showPet;

  return (
    <div className={styles.patientNameAndIndicators}>
      <TextWithEllipsis
        element="span"
        title={patientName}
        maxWidth="unset"
        size="small"
        weight="medium"
      >
        {patientName}
      </TextWithEllipsis>
      {hasPatientInfoIndicators ? (
        <div className={styles.patientInfoIndicators}>
          {showPaused ? <PausedIndicator /> : null}
          {showFlags && patient.flags ? (
            <FlagsPopover details={patient.flags} />
          ) : null}
          {showKeyToPatient && patient.address.keyToPatient ? (
            <KeyToPatientPopover details={patient.address.keyToPatient} />
          ) : null}
          {showPet && patient.pet ? <PetPopover details={patient.pet} /> : null}
        </div>
      ) : null}
    </div>
  );
};

const ActivityOccurrenceLabel = ({
  activityOccurrenceOrGroup,
}: {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
}) => {
  const isGroup = isGroupCheck(activityOccurrenceOrGroup);

  const activityOccurrenceLabel = isGroup
    ? activityOccurrenceOrGroup.occurrences
      ? activityOccurrenceOrGroup.occurrences
          .map((occurrence) => occurrence.title)
          .join(", ")
      : undefined
    : activityOccurrenceOrGroup.title;

  if (!activityOccurrenceLabel) {
    return null;
  }

  return (
    <TextWithEllipsis
      element="div"
      size={activityOccurrenceOrGroup.patient ? "smallest" : "small"}
      title={activityOccurrenceLabel}
      maxWidth="unset"
    >
      {activityOccurrenceLabel}
    </TextWithEllipsis>
  );
};

const Competences = ({
  activityOccurrenceOrGroup,
}: {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
}) => {
  const requiredCompetences = isEmployeePerformed(activityOccurrenceOrGroup)
    ? activityOccurrenceOrGroup.requiredCompetences
    : undefined;

  const doubleStaffing =
    "doubleStaffing" in activityOccurrenceOrGroup
      ? activityOccurrenceOrGroup.doubleStaffing
      : undefined;

  const hasCompetences = requiredCompetences && requiredCompetences.length > 0;
  const hasDoubleStaffing = doubleStaffing;

  if (!hasCompetences && !hasDoubleStaffing) {
    return null;
  }

  return (
    <ul className={styles.competences}>
      {hasCompetences
        ? requiredCompetences.map((competence) => (
            <li key={competence}>
              <CompetenceChip
                competence={competence}
                state="unassigned"
                key={competence}
                size="tiny"
              />
            </li>
          ))
        : null}
      {hasDoubleStaffing ? (
        <li>
          <RequirementChip
            requirement={activityRequirementSchema.enum.DoubleStaffing}
            state="unfulfilled"
            size="tiny"
          />
        </li>
      ) : null}
    </ul>
  );
};

const Time = ({
  activityOccurrenceOrGroup,
}: {
  activityOccurrenceOrGroup: Pick<
    IActivityOccurrenceOrGroup,
    "start" | "end" | "timeOfDay"
  >;
}) => {
  const { start, end, timeOfDay } = activityOccurrenceOrGroup;

  return (
    <TimeSpan
      size="smallest"
      color="faded"
      timespan={
        timeOfDay === timeOfDaySchema.enum.Any ? timeOfDay : { start, end }
      }
    />
  );
};

const ActivityOccurrenceList = ({
  activityOccurrenceOrGroup,
}: {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
}) => {
  const isGroup = isGroupCheck(activityOccurrenceOrGroup);

  return isGroup ? (
    activityOccurrenceOrGroup.occurrences ? (
      <ul>
        {activityOccurrenceOrGroup.occurrences.map((occurrence) => (
          <li key={`${occurrence.id} ${occurrence.activityId}`}>
            <ActivityTitle
              activityOccurrence={occurrence}
              size="smallest"
              showDescriptionAndComment
              showRecurringIcon
              weight="regular"
            />
          </li>
        ))}
      </ul>
    ) : null
  ) : (
    <ActivityTitle
      activityOccurrence={activityOccurrenceOrGroup}
      size="smallest"
      showDescriptionAndComment
      showRecurringIcon
      weight="regular"
    />
  );
};

export const AllocatableItemTile = ({
  activityOccurrenceOrGroup,
  isSelected,
}: {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
  isSelected: boolean;
}) => {
  const { isExpanded } = usePlanningViewToolbar();

  const { data: teams } = useTeams();
  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const duration = isGroupCheck(activityOccurrenceOrGroup)
    ? calculateGroupDuration(
        activityOccurrenceOrGroup.occurrences.map(
          (occurrence) => occurrence.duration,
        ),
      )
    : activityOccurrenceOrGroup.duration;

  const { patient } = activityOccurrenceOrGroup;

  const categoryAllowsDuration = getActivityCategoryConfig({
    category: activityOccurrenceOrGroup.category,
    activityHasPatient: Boolean(patient),
  }).allowsDuration;

  const shouldShowTeamForPatient =
    teams &&
    teams.length > 0 &&
    patient &&
    patient.status !== patientStatusSchema.enum.deleted &&
    patient.team;

  const isNonGroupActivityWithoutPatient =
    !isGroupCheck(activityOccurrenceOrGroup) && !patient;
  const hasDescriptionOrNote =
    isNonGroupActivityWithoutPatient &&
    (activityOccurrenceOrGroup.description || activityOccurrenceOrGroup.note);

  return (
    <div
      className={clsx(
        styles.tileContainer,
        isSelected ? styles.selected : undefined,
      )}
    >
      <div className={styles.dragHandle}>
        <DragHandle />
      </div>
      <div className={styles.innerTileContainer}>
        <div className={styles.categoryIcon}>
          <CategoryIcon
            category={activityOccurrenceOrGroup.category}
            size="tiny"
          />
        </div>
        {activityOccurrenceOrGroup.patient ? (
          <div className={styles.patientName}>
            <PatientName patient={activityOccurrenceOrGroup.patient} />
          </div>
        ) : null}
        {isExpanded && patient ? (
          <div className={styles.activityOccurrenceList}>
            <ActivityOccurrenceList
              activityOccurrenceOrGroup={activityOccurrenceOrGroup}
            />
          </div>
        ) : (
          <div className={styles.activityTitles}>
            <ActivityOccurrenceLabel
              activityOccurrenceOrGroup={activityOccurrenceOrGroup}
            />
          </div>
        )}
        {isExpanded && hasDescriptionOrNote ? (
          <div className={styles.activityNoPatientDescription}>
            {activityOccurrenceOrGroup.description ? (
              <TextWithLineBreaks size="smallest" color="faded">
                {activityOccurrenceOrGroup.description}
              </TextWithLineBreaks>
            ) : null}
            {activityOccurrenceOrGroup.note ? (
              <TextWithLineBreaks size="smallest" color="faded">
                {activityOccurrenceOrGroup.note}
              </TextWithLineBreaks>
            ) : null}
          </div>
        ) : null}
        <div className={styles.otherInfo}>
          {shouldShowTeamForPatient && patient.team ? (
            <div className={styles.team}>
              <TeamBadge team={patient.team} size="small" width="fit-content" />
            </div>
          ) : null}
          <Competences activityOccurrenceOrGroup={activityOccurrenceOrGroup} />
          <div className={styles.time}>
            <Time activityOccurrenceOrGroup={activityOccurrenceOrGroup} />
          </div>
          {showDuration && categoryAllowsDuration ? (
            <div className={styles.duration}>
              <DurationChip minutes={duration} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
